// Wake word detection, fully on-device.
//
// Runs openWakeWord in the browser via ONNX Runtime Web. Nothing leaves the
// robot: mic audio goes straight into three chained ONNX models and out comes a
// score. No network, no cost, no Dograh session until someone actually calls
// for Misti.
//
//   mic (16 kHz PCM)
//     -> melspectrogram.onnx    mel features
//        -> embedding_model.onnx   96-dim speech embeddings
//           -> <wakeword>.onnx        score 0..1
//
// Why this matters beyond UX: without a wake word the Dograh session stays open
// and is billed per minute even in an empty room. Gating on a wake word (or on
// presence detection, see README-DOGRAH.md) is what stops that.
//
// TWO HARD BROWSER RULES:
//   1. Must be served from https:// or http://localhost. AudioWorklet and mic
//      access both refuse anything else - a plain-HTTP LAN IP will fail.
//   2. The .onnx files must be same-origin. They live in public/openwakeword/.

let oww = null;
let mic = null;
let running = false;

// Pause and resume each take a moment (the mic stops and starts asynchronously).
// Run back to back - a line from the phone app that fails at once, then another
// - they would overlap: a pause landing mid-resume sees "not running" and does
// nothing, and two resumes both start the mic. So they queue, one at a time.
let micOp = Promise.resolve();
const oneAtATime = (fn) => (micOp = micOp.then(fn, fn));

/**
 * Loads the models and starts listening.
 *
 * @param {object}   opts
 * @param {function} opts.onWake    called as ({ label, score }) on detection
 * @param {string[]} opts.models    model names, e.g. ["hey_jarvis"]
 * @param {number}   opts.threshold 0..1, higher = fewer false triggers
 */
export async function startWakeWord({ onWake, models, threshold } = {}) {
	if (running) return true;

	// Defaults to Misti's own model, so a deployment that sets no variables still
	// answers to "Hey Misti" rather than to the stock "Hey Jarvis".
	const modelNames = models || (process.env.REACT_APP_WAKE_WORD_MODELS || "hey_misti.onnx").split(",").map((s) => s.trim());
	const thresh = threshold != null ? threshold : Number(process.env.REACT_APP_WAKE_WORD_THRESHOLD || 0.8);

	try {
		const { OpenWakeWord } = await import("openwakeword-web");
		const { Microphone } = await import("openwakeword-web/microphone");

		oww = await OpenWakeWord.create({
			baseUrl: process.env.PUBLIC_URL + "/openwakeword/models/",
			wakewordModels: modelNames,
			threshold: thresh,
			onDetection: ({ label, score }) => {
				console.log(`[wake] ${label} ${score.toFixed(2)}`);
				if (onWake) onWake({ label, score });
			},
		});

		// frame is an Int16Array of 1280 samples (80 ms @ 16 kHz)
		mic = new Microphone(async (frame) => {
			try {
				await oww.predict(frame);
			} catch (err) {
				console.error("[wake] predict failed:", err);
			}
		});

		await mic.start();
		running = true;
		console.log(`[wake] listening for: ${modelNames.join(", ")} (threshold ${thresh})`);
		return true;
	} catch (err) {
		console.error("[wake] could not start wake word detection:", err);
		running = false;
		return false;
	}
}

/**
 * Releases the microphone while Dograh has the call.
 *
 * Two live getUserMedia streams on one device fight over echo cancellation and
 * automatic gain, so the detector must let go for the duration of the call.
 */
export function pauseWakeWord() {
	return oneAtATime(async () => {
		if (!running || !mic) return;
		try {
			await mic.stop();
			running = false;
			console.log("[wake] paused, mic released to Dograh");
		} catch (err) {
			console.error("[wake] pause failed:", err);
		}
	});
}

/** Takes the microphone back after the call ends. */
export function resumeWakeWord() {
	return oneAtATime(async () => {
		if (running || !mic) return;
		try {
			await mic.start();
			running = true;
			console.log("[wake] listening again");
		} catch (err) {
			console.error("[wake] resume failed:", err);
		}
	});
}

export async function stopWakeWord() {
	await pauseWakeWord();
	oww = null;
	mic = null;
}

export function isWakeWordRunning() {
	return running;
}

/** Raise to cut false triggers, lower if it misses. Takes effect immediately. */
export function setWakeWordThreshold(v) {
	if (oww) oww.threshold = v;
}

const wakeWord = {
	startWakeWord,
	pauseWakeWord,
	resumeWakeWord,
	stopWakeWord,
	isWakeWordRunning,
	setWakeWordThreshold,
};

export default wakeWord;
